import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Box } from '@mui/system'
import { CardMedia, Divider, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { MinusCircleIcon, PlusCircleIcon, XMarkIcon } from '~/assets/icons'
import { convertPrice } from '~/utils'
import { changeQuantityItemCart, priceTotalItemCart, removeItemCart } from '~/redux/cartSlice'
import { FoodItemTopping, ToolTip } from '~/components'
import { imageMissing } from '~/assets/images'
import { handleErrorImg } from '~/utils/handleErrorImage'
import { ThemeContext } from '~/theme/ThemeProvider'

export const MAX_QUANTITY_CART = 99
export const MIN_QUANTITY_CART = 1
export const NUMBER_INCREMENT = 1
export const NUMBER_DECREASE = -1

const CartItem = ({
  name,
  price,
  original_price = 0,
  discount_amount = 0,
  note,
  image,
  quantity,
  foodOptionTypes = [],
  primaryKey,
  isHidePlusFoodItemEnable,
}) => {
  const [currentQuantity, setCurrentQuantity] = useState(quantity)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { currentThemeObject } = useContext(ThemeContext)
  const isHidePlusFoodItem = isHidePlusFoodItemEnable && price === 0

  const [open, setOpen] = useState(false)

  const openToolTip = () => {
    setOpen(true)
  }

  const closeToolTip = () => {
    setOpen(false)
  }

  const changeQuantityHandler = (numQuantity) => {
    if (currentQuantity <= MIN_QUANTITY_CART && numQuantity === NUMBER_DECREASE) {
      openToolTip()
      return
    }

    if (currentQuantity >= MAX_QUANTITY_CART && numQuantity === NUMBER_INCREMENT) return
    setCurrentQuantity((prevQuantity) => {
      return prevQuantity + numQuantity
    })

    dispatch(changeQuantityItemCart({ primaryKey, numQuantity }))
  }

  const removeItemCartHandler = () => {
    dispatch(removeItemCart(primaryKey))
  }

  const renderContentTooltip = () => {
    return (
      <Box display="flex" alignItems="center">
        <Typography
          onClick={removeItemCartHandler}
          fontWeight="600"
          padding="12px"
          fontSize="1.6rem"
          component="div"
          color="red">
          {t('actions.delete_dish')}
        </Typography>
        <Typography padding="12px" onClick={closeToolTip}>
          <XMarkIcon size="12" />
        </Typography>
      </Box>
    )
  }

  const renderDecreseBtn = () => {
    return (
      <ToolTip open={open} contentToolTip={renderContentTooltip()} onClose={closeToolTip}>
        <Typography
          lineHeight="0"
          paddingY="10px"
          paddingRight="8px"
          onClick={() => changeQuantityHandler(NUMBER_DECREASE)}>
          <MinusCircleIcon color={currentThemeObject.colorText1} />
        </Typography>
      </ToolTip>
    )
  }

  const renderActionsQuantity = () => {
    return (
      <Box display="flex" alignItems="center" flexWrap="wrap">
        {renderDecreseBtn()}
        <Typography fontSize="1.6rem" fontWeight="600" color={'baseColor.colorText1'}>
          {currentQuantity}
        </Typography>
        <Typography
          lineHeight="0"
          paddingY="8px"
          paddingLeft="8px"
          visibility={isHidePlusFoodItem ? 'hidden' : 'visible'}
          onClick={() => changeQuantityHandler(NUMBER_INCREMENT)}>
          <PlusCircleIcon color={currentThemeObject.colorText1} />
        </Typography>
      </Box>
    )
  }

  const renderImageItemCart = () => {
    return (
      <CardMedia
        onError={handleErrorImg}
        image={image.url || imageMissing}
        src="cart image"
        sx={styles.image}
      />
    )
  }

  // Hiển thị giá gốc đã bao gồm topping
  const renderOriginalPrice = () => {
    if (!discount_amount) return null

    const originalPrice = priceTotalItemCart({
      price: original_price,
      foodOptionTypes,
    })

    return (
      <Box
        lineHeight="1.2"
        fontSize="1rem"
        fontWeight="400"
        color="baseColor.colorText1"
        sx={{ textDecoration: 'line-through' }}>
        {convertPrice(originalPrice)}
      </Box>
    )
  }

  // Hiển thị giá khuyến mãi bao gồm topping
  const renderPromotionalPrice = () => {
    const promotionalPrice = priceTotalItemCart({
      price,
      foodOptionTypes,
    })

    return (
      <Box
        lineHeight="1.2"
        fontSize="1.4rem"
        fontWeight="600"
        color={discount_amount ? 'baseColor.colorText7' : 'baseColor.colorText1'}>
        {convertPrice(promotionalPrice)}
      </Box>
    )
  }

  // Hiển thị giá (đã bao gồm topping) trước và sau khi giảm giá
  const renderPrice = () => {
    return (
      <Box mt="4px">
        {renderOriginalPrice()}
        {renderPromotionalPrice()}
      </Box>
    )
  }

  const renderInfoItemCart = () => {
    return (
      <Box marginLeft="12px" flex={1}>
        <Typography
          component="h4"
          fontSize="1.8rem"
          lineHeight="2.2rem"
          fontWeight="600"
          color={'baseColor.colorText1'}>
          {name}
        </Typography>
        <FoodItemTopping foodOptionTypes={foodOptionTypes} color={'baseColor.colorText1'} />
        <Typography fontSize="1.4rem" fontStyle="italic" color={'baseColor.colorText1'}>
          {note}
        </Typography>
        {renderPrice()}
      </Box>
    )
  }

  return (
    <Box>
      <Box padding="24px 0px 16px 0px" display="flex" alignItems="center">
        {renderImageItemCart()}
        {renderInfoItemCart()}
        {renderActionsQuantity()}
      </Box>
      <Divider sx={{ borderColor: 'baseColor.colorText1' }} />
    </Box>
  )
}

export default React.memo(CartItem)

CartItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  price: PropTypes.number,
  original_price: PropTypes.number,
  discount_amount: PropTypes.number,
  foodOptionTypes: PropTypes.array,
  note: PropTypes.string,
  image: PropTypes.object,
  quantity: PropTypes.number,
  primaryKey: PropTypes.string,
  isHidePlusFoodItemEnable: PropTypes.bool,
}

const styles = {
  image: {
    width: '85px',
    height: '85px',
    borderRadius: '15px',
  },
}
